import React, { useEffect, useState } from "react";
import { listNotifications, retryNotification, EMAIL_STATUS } from "../../services/api/adminService";
import { ERROR_CODES } from "../../services/errors/ApiError";
import { isAuthError, isCanceledError, isErrorCode } from "../../services/errors/errorHandler";
import { formatBusinessDateTime } from "../../services/utils/dateUtils";

/** What each stored email is (backend notification_outbox.kind). */
export const EMAIL_KIND_LABELS = {
  client_confirmation: "Booking confirmation to the client",
  owner_new_booking: "New-booking alert to you",
  client_cancellation: "Cancellation notice to the client",
  owner_cancellation: "Cancellation alert to you",
  client_reschedule: "Reschedule notice to the client",
  owner_reschedule: "Reschedule alert to you",
  owner_contact_message: "Contact-form message alert to you",
  client_meeting_link: "Session link to the client",
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "2 will be retried automatically. 1 gave up and needs Retry." */
export const summarizeCounts = ({ failed = 0, dead = 0 }) =>
  [
    failed > 0 && `${plural(failed, "email", "emails")} will be retried automatically.`,
    dead > 0 && `${plural(dead, "email", "emails")} stopped retrying and ${dead === 1 ? "needs" : "need"} a manual Retry.`,
  ]
    .filter(Boolean)
    .join(" ");

const retryErrorMessage = (err) => {
  if (isErrorCode(err, ERROR_CODES.SERVICE_UNAVAILABLE)) {
    return "Email is switched off or not configured on the server, so nothing can be sent. Check the MAIL_* settings.";
  }
  return err.message || "Could not retry that email. Please try again.";
};

function EmailRow({ row, busy, onRetry }) {
  const dead = row.status === EMAIL_STATUS.DEAD;
  return (
    <li className="admin-email" data-notification-id={row.id}>
      <div className="admin-email__head">
        <strong>{EMAIL_KIND_LABELS[row.kind] || row.kind}</strong>
        <span className={`admin-badge admin-badge--email-${row.status}`}>
          {dead ? "Stopped retrying" : "Will retry"}
        </span>
      </div>
      <dl className="admin-fields admin-fields--compact">
        <div><dt>To</dt><dd>{row.recipient || "—"}</dd></div>
        <div><dt>Subject</dt><dd>{row.subject || "—"}</dd></div>
        {row.reference && <div><dt>Booking</dt><dd>{row.reference}</dd></div>}
        <div><dt>Attempts</dt><dd>{row.attempts ?? 0}</dd></div>
        {row.created_at && <div><dt>Queued</dt><dd>{formatBusinessDateTime(row.created_at)}</dd></div>}
        {!dead && row.next_attempt_at && (
          <div><dt>Next automatic try</dt><dd>{formatBusinessDateTime(row.next_attempt_at)}</dd></div>
        )}
      </dl>
      {row.last_error && <p className="admin-email__error">Last error: {row.last_error}</p>}
      <button type="button" className="admin-btn" disabled={busy} onClick={() => onRetry(row)}>
        Retry
      </button>
    </li>
  );
}

/**
 * Banner on the owner's bookings page when stored emails (booking
 * confirmations, alerts, session links) failed to send: GET
 * /api/admin/notifications (failed + dead), each with a Retry action
 * (POST /api/admin/notifications/<id>/retry). Renders nothing while
 * every email went out.
 *
 * @param {object} props
 * @param {number} [props.refreshKey] - change it to reload (the page's Refresh)
 */
export default function EmailAlerts({ refreshKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ failed: 0, dead: 0 });
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    setNotice("");
    setActionError("");
    listNotifications({ status: [EMAIL_STATUS.FAILED, EMAIL_STATUS.DEAD], signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setRows(data.notifications);
        setCounts({ failed: data.counts.failed, dead: data.counts.dead });
        setTotal(data.total);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        if (!isAuthError(err)) setLoadError(err.message || "Could not check for unsent emails.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [refreshKey]);

  const dropRow = (row) => {
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    setTotal((t) => Math.max(0, t - 1));
    setCounts((prev) => ({ ...prev, [row.status]: Math.max(0, (prev[row.status] || 0) - 1) }));
  };

  const retry = async (row) => {
    if (busyId !== null) return;
    setBusyId(row.id);
    setNotice("");
    setActionError("");
    try {
      const updated = await retryNotification(row.id);
      if (updated && updated.status === EMAIL_STATUS.SENT) {
        dropRow(row);
        setNotice("Email sent.");
      } else {
        const next = { ...row, ...(updated || {}) };
        setRows((prev) => prev.map((r) => (r.id === row.id ? next : r)));
        if (next.status !== row.status) {
          setCounts((prev) => ({
            ...prev,
            [row.status]: Math.max(0, (prev[row.status] || 0) - 1),
            [next.status]: (prev[next.status] || 0) + 1,
          }));
        }
        setActionError(
          `Still could not send that email${next.last_error ? `: ${next.last_error}` : "."}`
        );
      }
    } catch (err) {
      if (isAuthError(err)) return;
      if (isErrorCode(err, ERROR_CODES.INVALID_STATE)) {
        // Sent in the meantime (e.g. by the automatic retry).
        dropRow(row);
        setNotice("That email has already been sent.");
      } else {
        setActionError(retryErrorMessage(err));
      }
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;

  if (loadError) {
    return (
      <p className="admin-muted" data-testid="email-alerts-error">
        Could not check for unsent emails: {loadError}
      </p>
    );
  }

  if (total === 0) {
    return notice ? (
      <div aria-live="polite" className="admin-live">
        <p className="admin-notice">{notice}</p>
      </div>
    ) : null;
  }

  const summary = summarizeCounts(counts);
  return (
    <section className="admin-alert" aria-labelledby="email-alerts-title" data-testid="email-alerts">
      <h2 id="email-alerts-title" className="admin-alert__title">
        {plural(total, "email", "emails")} could not be sent
      </h2>
      {summary && <p className="admin-alert__text">{summary}</p>}
      <button
        type="button"
        className="admin-btn"
        aria-expanded={open}
        aria-controls="email-alerts-list"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide emails" : "Review emails"}
      </button>
      <div aria-live="polite" className="admin-live">
        {notice && <p className="admin-notice">{notice}</p>}
      </div>
      {actionError && <p className="admin-error" role="alert">{actionError}</p>}
      {open && (
        <>
          <ul id="email-alerts-list" className="admin-email-list">
            {rows.map((row) => (
              <EmailRow key={row.id} row={row} busy={busyId !== null} onRetry={retry} />
            ))}
          </ul>
          {rows.length < total && (
            <p className="admin-muted">Showing the newest {rows.length} of {total}.</p>
          )}
        </>
      )}
    </section>
  );
}
